import { metaRequestContext, trackCheckoutStarted } from "../lib/metaIntentTracking";
import { loadStripe } from "@stripe/stripe-js";
import React, { createContext, useContext, useEffect, useState } from "react";
import { isEntitledSubscriptionStatus } from "../utils/billingStatus";
import { useAuth } from "./AuthContext";
import { storeBillingReturnIntent } from "../utils/billingReturnIntent";
import posthog from "posthog-js";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API_KEY!);

// Navigate straight to the session's hosted page, like resumeCheckoutSession.
// redirectToCheckout is deprecated and needs Stripe.js to load and run in the
// browser; it hung silently for a Safari user after the session was created.
// It stays only as a fallback for a server that doesn't return `url` yet.
async function goToCheckout(
  session: { sessionId: string; url?: string | null; reused?: boolean },
  mode: "subscription" | "credit_pack",
) {
  posthog.capture("checkout_redirect_started", {
    checkout_session_id: session.sessionId,
    method: session.url ? "url" : "stripe_js",
    mode,
    reused: session.reused === true,
  });
  if (session.url) {
    window.location.href = session.url;
    return;
  }
  const stripe = await stripePromise;
  const { error } = await stripe!.redirectToCheckout({
    sessionId: session.sessionId,
  });
  if (error) {
    console.error("Stripe checkout redirection error:", error);
    throw error;
  }
}

interface Plan {
  id: string;
  name: string;
  price: number;
  interval: "month" | "year";
  currentPeriodEndAt?: number;
  creditRefreshAt?: number;
  cancelAtPeriodEnd?: boolean;
  cancelAt?: number;
  canceledAt?: number;
  subscriptionStatus?: string;
  maxConnections?: number;
  features?: string[]; // Ensure this matches the backend structure if used
  description?: string; // Ensure this matches the backend structure if used
  video_credits: number; // Credits offered by the plan
  brand_kits?: number; // Number of brand kits allowed, undefined means unlimited
  tier?: string; // Plan tier: "lite", "starter", "pro", "business"
  teamEnabled?: boolean;
  maxMembers?: number | null;
}

interface CreditPack {
  id: string;
  name: string;
  description?: string | null;
  price: number;
  currency: string;
  video_credits: number;
}

// Details for the user's plan configuration, combining DB and Stripe info
interface CurrentPlanDetails {
  id: string; // The plan identifier from Firestore DB (e.g., "free", "pro_legacy", a Stripe Price ID)
  planDetail: Plan | null; // Full Plan object (name, features, video_credits offered) matched from availablePlans, or null if no match
  stripePriceId: string | null; // Price ID only when the Stripe subscription is entitled (active/trialing)
  creditRefreshAt?: number;
  currentPeriodEndAt?: number;
  cancelAtPeriodEnd?: boolean;
  cancelAt?: number;
  canceledAt?: number;
  subscriptionStatus?: string;
  paymentStatus?: string;
}

type BillingPortalFlow = "payment_method_update";

interface StripeContextType {
  createCheckoutSession: (
    priceId: string,
    successUrl?: string,
    allowPromo?: boolean,
  ) => Promise<void>;
  createCreditCheckoutSession: (
    priceId: string,
    successUrl?: string,
  ) => Promise<void>;
  resumeCheckoutSession: (sessionId: string) => Promise<void>;
  currentPlan: CurrentPlanDetails | null; // Updated type
  availablePlans: Plan[];
  availableCreditPacks: CreditPack[];
  isLoading: boolean;
  createPortalSession: (flow?: BillingPortalFlow) => Promise<void>;
  createPaymentRecoverySession: () => Promise<void>;
  isHighestTier: boolean; // Added
  isFreePlan: boolean; // Added - centralized free plan detection
  isStarterOrAbove: boolean; // Lite users don't get first comment, autopilot
  maxConnections: number; // Channels this workspace may connect
}

const FREE_PLAN_MAX_CONNECTIONS = 1;

const StripeContext = createContext<StripeContextType | undefined>(undefined);

export function useStripe() {
  const context = useContext(StripeContext);
  if (!context) {
    throw new Error("useStripe must be used within StripeProvider");
  }
  return context;
}

// Test user override — bypass plan gates for internal testing in production
const TEST_USER_UID = "XCE11up6ZTbjeUwcgTtjHCMdBeh1";

export function StripeProvider({ children }: { children: React.ReactNode }) {
  const { currentUser, activeWorkspaceId, customerData } = useAuth();
  const isTestUser = currentUser?.uid === TEST_USER_UID;
  const [currentPlan, setCurrentPlan] = useState<CurrentPlanDetails | null>(
    null,
  ); // Updated type
  const [availablePlans, setAvailablePlans] = useState<Plan[]>([]);
  const [availableCreditPacks, setAvailableCreditPacks] = useState<
    CreditPack[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const requestEpochRef = React.useRef(0);
  useEffect(() => {
    const requestEpoch = ++requestEpochRef.current;
    const controller = new AbortController();
    const isCurrent = () => requestEpochRef.current === requestEpoch;
    setCurrentPlan(null);
    setAvailablePlans([]);
    setAvailableCreditPacks([]);
    const fetchData = async () => {
      if (!currentUser || !activeWorkspaceId) {
        if (isCurrent()) setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        // 1. Fetch available plans from Stripe (via our backend).
        // A failure here must NOT abort the whole context — otherwise a transient
        // /plans error would null currentPlan and blank availablePlans, silently
        // downgrading a paying user to free-tier UI. Degrade gracefully instead;
        // paid/tier is still recovered from the Firestore customer doc below.
        let plansData: Plan[] = [];
        try {
          const plansResponse = await fetch(
            `${process.env.REACT_APP_API_BASE}/stripe/plans`,
            { signal: controller.signal },
          );
          if (plansResponse.ok) {
            plansData = await plansResponse.json();
          } else {
            console.error("Failed to fetch plans:", await plansResponse.text());
          }
        } catch (plansError) {
          console.error("Error fetching plans:", plansError);
        }
        if (!isCurrent()) return;
        setAvailablePlans(plansData);

        try {
          const creditPacksResponse = await fetch(
            `${process.env.REACT_APP_API_BASE}/stripe/credit-packs`,
            { signal: controller.signal },
          );
          if (creditPacksResponse.ok) {
            const creditPacksData: CreditPack[] =
              await creditPacksResponse.json();
            if (isCurrent()) setAvailableCreditPacks(creditPacksData);
          } else {
            console.warn(
              "Failed to fetch credit packs:",
              await creditPacksResponse.text(),
            );
            if (isCurrent()) setAvailableCreditPacks([]);
          }
        } catch (creditPackError) {
          console.error("Error fetching credit packs:", creditPackError);
          if (isCurrent() && !controller.signal.aborted) {
            setAvailableCreditPacks([]);
          }
        }

        // 2. Use the server-sanitized workspace billing summary. Browsers no
        // longer read the raw customer ledger (which contains Stripe secrets).
        const dbPlanIdentifier = customerData?.plan || "free";
        // 3. Fetch this workspace's current Stripe billing relationship. The
        // endpoint also returns recoverable non-active subscriptions without
        // granting paid-plan access.
        let determinedStripePriceId: string | null = null;
        let billingSubscriptionPlan: Plan | null = null;
        let billingPaymentStatus: string | undefined =
          customerData?.paymentStatus;
        // Tracks whether the live subscription lookup actually FAILED (network /
        // server error) vs. succeeded and reported no subscription. A genuinely
        // free user gets a 200 with { plan: null }; only a real failure should
        // trigger the cached-plan fallback below.
        let subscriptionLookupFailed = false;
        try {
          const token = await currentUser.getIdToken();
          const subResponse = await fetch(
            `${process.env.REACT_APP_API_BASE}/stripe/subscription/${activeWorkspaceId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "x-workspace-id": activeWorkspaceId,
              },
              signal: controller.signal,
            },
          );
          if (subResponse.ok) {
            const subData = await subResponse.json();
            billingPaymentStatus =
              subData?.paymentStatus ?? billingPaymentStatus;
            if (subData && subData.plan && subData.plan.id) {
              billingSubscriptionPlan = subData.plan;
              // During a rolling deploy the older backend only returns active
              // subscriptions and may omit status, so keep that response
              // entitled. The new endpoint also returns non-active billing
              // relationships for recovery without granting plan access.
              if (
                !subData.plan.subscriptionStatus ||
                isEntitledSubscriptionStatus(subData.plan.subscriptionStatus)
              ) {
                determinedStripePriceId = subData.plan.id;
              }
            }
          } else {
            // Non-OK is a real error — free users get 200 + { plan: null }.
            subscriptionLookupFailed = true;
            const errorBodyForLogging = await subResponse.text();
            console.warn(
              `Stripe subscription fetch status: ${subResponse.status}`,
              errorBodyForLogging,
            );
          }
        } catch (stripeSubError) {
          if (controller.signal.aborted) throw stripeSubError;
          subscriptionLookupFailed = true;
          console.error(
            "Error fetching Stripe subscription details:",
            stripeSubError,
          );
          // Do not throw, allow app to proceed with DB plan info
        }

        // Only when the live lookup actually FAILED, fall back to the plan
        // recorded on the Firestore customer doc, so a transient Stripe/endpoint
        // error doesn't downgrade a paying user. We must NOT do this when the
        // lookup succeeded and reported no subscription: the customer doc's
        // `plan` can be stale (a non-"free" price id left over from a past
        // subscription), which would otherwise mark a free user as paid.
        if (
          subscriptionLookupFailed &&
          !determinedStripePriceId &&
          dbPlanIdentifier &&
          dbPlanIdentifier !== "free"
        ) {
          determinedStripePriceId = dbPlanIdentifier;
        }

        // 4. Determine the plan detail to use
        let matchedPlanDetail: Plan | null = null;
        if (billingSubscriptionPlan) {
          // Keep plan details for past-due/unpaid subscriptions so the UI can
          // offer billing recovery without treating the user as entitled.
          matchedPlanDetail = billingSubscriptionPlan;
        } else {
          // Fallback: try to find in availablePlans (for non-Stripe plans or edge cases)
          matchedPlanDetail =
            plansData.find(
              (p) =>
                p.id === dbPlanIdentifier ||
                p.name.toLowerCase() === dbPlanIdentifier,
            ) || null;
        }

        const finalPlan = {
          id: matchedPlanDetail?.id || dbPlanIdentifier,
          planDetail: matchedPlanDetail,
          stripePriceId: determinedStripePriceId,
          currentPeriodEndAt: billingSubscriptionPlan?.currentPeriodEndAt,
          creditRefreshAt:
            billingSubscriptionPlan?.creditRefreshAt ||
            (!billingSubscriptionPlan?.cancelAtPeriodEnd
              ? billingSubscriptionPlan?.currentPeriodEndAt
              : undefined),
          cancelAtPeriodEnd: billingSubscriptionPlan?.cancelAtPeriodEnd,
          cancelAt: billingSubscriptionPlan?.cancelAt,
          canceledAt: billingSubscriptionPlan?.canceledAt,
          subscriptionStatus: billingSubscriptionPlan?.subscriptionStatus,
          paymentStatus: billingPaymentStatus,
        };
        // upgrade_completed is tracked server-side in the Stripe webhook

        // 5. Test user override — grant pro-level limits for internal testing
        if (isTestUser) {
          finalPlan.planDetail = {
            ...(finalPlan.planDetail || {
              id: "test",
              name: "Test",
              price: 0,
              interval: "month" as const,
              video_credits: 100,
            }),
            maxConnections: 5,
          };
          finalPlan.stripePriceId = finalPlan.stripePriceId || "test_override";
        }

        // 6. Set the comprehensive currentPlan state
        if (isCurrent()) setCurrentPlan(finalPlan);
      } catch (error) {
        if (controller.signal.aborted || !isCurrent()) return;
        console.error("Error fetching Stripe data for context:", error);
        setCurrentPlan(null); // Reset on major error
        setAvailablePlans([]);
        setAvailableCreditPacks([]);
      } finally {
        if (isCurrent()) setIsLoading(false);
      }
    };

    void fetchData();
    return () => {
      controller.abort();
      if (requestEpochRef.current === requestEpoch) {
        requestEpochRef.current += 1;
      }
    };
  }, [
    currentUser,
    activeWorkspaceId,
    customerData?.plan,
    customerData?.paymentStatus,
    customerData?.lastInvoiceId,
    isTestUser,
  ]);

  const isHighestTier = React.useMemo(() => {
    if (
      !currentPlan ||
      !currentPlan.planDetail ||
      availablePlans.length === 0
    ) {
      // No plan details or no plans to compare against means not on a highest paid tier.
      return false;
    }

    // Use the current plan's details (works for both active and inactive prices)
    const currentPlanCredits = currentPlan.planDetail.video_credits;
    if (typeof currentPlanCredits === "undefined") {
      return false;
    }

    // Find the maximum video_credits offered among all *currently available* plans
    // (availablePlans contains only active prices for new customers)
    const maxOfferedCredits = Math.max(
      ...availablePlans.map((plan) => plan.video_credits ?? 0),
    );

    // User is on highest tier if their plan offers >= the maximum currently available
    return currentPlanCredits >= maxOfferedCredits;
  }, [currentPlan, availablePlans]);

  // Centralized free plan detection
  // A user is on a free plan if they don't have an active Stripe subscription
  const isFreePlan = React.useMemo(() => {
    if (isTestUser) return false;
    return !currentPlan?.stripePriceId;
  }, [currentPlan, isTestUser]);

  // Starter or above: any paid plan that is NOT Lite gets advanced features (first comment, autopilot).
  // Safe default: legacy prices missing tier metadata are treated as starter+.
  const isStarterOrAbove = React.useMemo(() => {
    if (isTestUser) return true;
    if (!currentPlan?.stripePriceId) return false;
    const tier = currentPlan.planDetail?.tier?.toLowerCase();
    return tier !== "lite";
  }, [currentPlan, isTestUser]);

  // Mirrors getMaxConnectionsForUser on the server: a per-workspace override
  // wins, otherwise the plan's limit, otherwise the free cap.
  const maxConnections = React.useMemo(() => {
    const override = customerData?.maxConnectionsOverride;
    if (typeof override === "number" && override > 0) return override;
    const planMax = currentPlan?.planDetail?.maxConnections;
    return typeof planMax === "number" ? planMax : FREE_PLAN_MAX_CONNECTIONS;
  }, [customerData?.maxConnectionsOverride, currentPlan?.planDetail?.maxConnections]);

  const createCheckoutSession = async (
    priceId: string,
    successUrl?: string,
    allowPromo?: boolean,
  ) => {
    if (!currentUser || !activeWorkspaceId) {
      throw new Error("No user logged in");
    }

    const token = await currentUser.getIdToken();
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE}/stripe/create-checkout-session`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-workspace-id": activeWorkspaceId,
        },
        body: JSON.stringify({
          priceId,
          marketing: metaRequestContext(),
          userId: activeWorkspaceId,
          email: currentUser.email,
          successUrl:
            successUrl || `${window.location.origin}/settings?success=true`,
          allowPromo,
          returnPath: `${window.location.pathname}${window.location.search}${window.location.hash}`,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Failed to create checkout session:", errorData);
      throw new Error(
        errorData.error || "Failed to initiate checkout session.",
      );
    }

    const data = await response.json();

    if (data.sessionId) {
      if (data.livemode === true) void trackCheckoutStarted(currentUser, activeWorkspaceId, data.sessionId);
      await goToCheckout(data, "subscription");
    } else if (data.portalUrl) {
      // Redirect to Stripe Billing Portal
      storeBillingReturnIntent(
        activeWorkspaceId,
        customerData?.pendingTeamEntitlement,
      );
      window.location.href = data.portalUrl;
    } else {
      // Handle unexpected response
      console.error("Unexpected response from server:", data);
      throw new Error("Received an unexpected response from the server.");
    }
  };

  const createCreditCheckoutSession = async (
    priceId: string,
    successUrl?: string,
  ) => {
    if (!currentUser) {
      throw new Error("No user logged in");
    }

    const token = await currentUser.getIdToken();
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE}/stripe/create-credit-checkout-session`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-workspace-id": activeWorkspaceId || "",
        },
        body: JSON.stringify({
          priceId,
          successUrl:
            successUrl ||
            `${window.location.origin}/generate?credits=success&credit_session_id={CHECKOUT_SESSION_ID}`,
          returnPath: `${window.location.pathname}${window.location.search}${window.location.hash}`,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Failed to create credit checkout session:", errorData);
      throw new Error(
        errorData.error || "Failed to initiate credit checkout session.",
      );
    }

    const data = await response.json();

    if (!data.sessionId) {
      console.error("Unexpected response from server:", data);
      throw new Error("Received an unexpected response from the server.");
    }

    await goToCheckout(data, "credit_pack");
  };

  // Ask the server to validate the stored session before sending the user to
  // Stripe. A stale session id would otherwise land on Stripe's dead-end
  // "You're all done here" page. Throws when the session cannot be resumed so
  // callers fall back to a fresh checkout.
  const resumeCheckoutSession = async (sessionId: string) => {
    if (!currentUser || !activeWorkspaceId) {
      throw new Error("No active workspace");
    }

    const token = await currentUser.getIdToken();
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE}/stripe/resume-checkout-session`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-workspace-id": activeWorkspaceId,
        },
        body: JSON.stringify({ sessionId, marketing: metaRequestContext() }),
      },
    );

    if (!response.ok) {
      let message = "Checkout session could not be resumed";
      try {
        const errorBody = await response.json();
        message = errorBody.error || message;
      } catch {
        // Keep the fallback message when the server does not return JSON.
      }
      console.warn("Stripe checkout resume unavailable:", message);
      throw new Error(message);
    }

    const { url, mode, kind, livemode } = await response.json();
    if (!url) throw new Error("Checkout resume URL was missing");
    if (kind === "open" && mode === "subscription" && livemode === true)
      void trackCheckoutStarted(currentUser, activeWorkspaceId, sessionId);
    window.location.href = url;
  };

  const createPortalSession = async (flow?: BillingPortalFlow) => {
    if (!currentUser || !activeWorkspaceId) {
      throw new Error("No active workspace");
    }

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/stripe/create-portal-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "x-workspace-id": activeWorkspaceId,
          },
          body: JSON.stringify({
            ...(flow ? { flow } : {}),
            returnPath:
              `${window.location.pathname}${window.location.search}${window.location.hash}`,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to create portal session");
      }

      const { url } = await response.json();
      if (activeWorkspaceId) {
        storeBillingReturnIntent(
          activeWorkspaceId,
          customerData?.pendingTeamEntitlement,
        );
      }
      window.location.href = url;
    } catch (error) {
      console.error("Error creating portal session:", error);
      throw error;
    }
  };

  const createPaymentRecoverySession = async () => {
    if (!currentUser || !activeWorkspaceId) {
      throw new Error("No active workspace");
    }

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/stripe/create-payment-recovery-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "x-workspace-id": activeWorkspaceId,
          },
        },
      );

      if (!response.ok) {
        let message = "Failed to create payment recovery session";
        try {
          const errorBody = await response.json();
          message = errorBody.error || message;
        } catch {
          // Keep the fallback error when the server does not return JSON.
        }
        throw new Error(message);
      }

      const { url } = await response.json();
      if (!url) throw new Error("Payment recovery URL was missing");
      window.location.href = url;
    } catch (error) {
      console.error("Error creating payment recovery session:", error);
      throw error;
    }
  };

  return (
    <StripeContext.Provider
      value={{
        createCheckoutSession,
        createCreditCheckoutSession,
        resumeCheckoutSession,
        currentPlan,
        isLoading,
        availablePlans,
        availableCreditPacks,
        createPortalSession,
        createPaymentRecoverySession,
        isHighestTier,
        isFreePlan, // Added - centralized free plan detection
        isStarterOrAbove,
        maxConnections,
      }}
    >
      {children}
    </StripeContext.Provider>
  );
}
